import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useT } from "../context/LanguageContext";
import { api } from "../lib/api";
import {
  ChevronLeft, Smartphone, Monitor, ShieldCheck, Lock, ArrowRight, Trophy, Calendar, Shield, Bell, BellOff, Loader2,
  List as ListIcon, Sparkles,
} from "lucide-react";
import { toast } from "sonner";
import Wc2026Logo from "../components/Wc2026Logo";
import WcRegisterBanner from "../components/WcRegisterBanner";
import WcJoinCard from "../components/WcJoinCard";
import WcLockedNotice from "../components/WcLockedNotice";
import useWcModeVisibility from "../hooks/useWcModeVisibility";

/**
 * WC 2026 Hub — iter 81 pure-typography redesign.
 *
 *  • No JPG poster in the hero. Big gradient "FIFA WORLD CUP" + huge gold
 *    "2026" + host country chips + facts strip + "UNITED · AS · ONE" band.
 *  • Locked view: 3 rule cards with custom icon blocks (Trophy / Calendar /
 *    Shield) + "Notify me when this opens" toggle per mode.
 *  • Gate: admins + moderators see full hub immediately. Everyone else sees
 *    the countdown + rules + Notify toggles until access opens 24h before
 *    kickoff (Jun 10, 2026 16:00 UTC = Jun 11 12:00 ET, -24h).
 */

// Kickoff: Jun 11, 2026 12:00 ET = 16:00 UTC (EDT is UTC-4 in June).
export const WC_KICKOFF_UTC = Date.UTC(2026, 5, 11, 16, 0, 0);  // months 0-indexed
export const WC_ACCESS_OPEN_UTC = WC_KICKOFF_UTC - 24 * 3600 * 1000;

export function useWcAccess() {
  const { user } = useAuth();
  const isStaff = user?.role === "admin" || user?.role === "moderator";
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  return {
    isStaff,
    publicOpen:    now >= WC_ACCESS_OPEN_UTC,
    kickoffPassed: now >= WC_KICKOFF_UTC,
    msUntilOpen:   Math.max(0, WC_ACCESS_OPEN_UTC - now),
    msUntilKickoff: Math.max(0, WC_KICKOFF_UTC - now),
    now,
  };
}

function pad2(n) { return String(n).padStart(2, "0"); }

export function CountdownClock({ targetMs, label = "Opens in" }) {
  const [tick, setTick] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const ms = Math.max(0, targetMs - tick);
  const totalSec = Math.floor(ms / 1000);
  const days  = Math.floor(totalSec / 86400);
  const hrs   = Math.floor((totalSec % 86400) / 3600);
  const mins  = Math.floor((totalSec % 3600) / 60);
  const secs  = totalSec % 60;
  return (
    <div className="text-center" data-testid="wc26-countdown">
      {label && (
        <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFD700]/80 mb-2">{label}</div>
      )}
      <div className="inline-flex items-center gap-2 sm:gap-3 font-mono tabular-nums">
        <Tile v={days} l="D" />
        <span className="text-[#FFD700]/40 text-2xl">:</span>
        <Tile v={pad2(hrs)} l="H" />
        <span className="text-[#FFD700]/40 text-2xl">:</span>
        <Tile v={pad2(mins)} l="M" />
        <span className="text-[#FFD700]/40 text-2xl">:</span>
        <Tile v={pad2(secs)} l="S" />
      </div>
    </div>
  );
}

function Tile({ v, l }) {
  return (
    <div className="relative">
      <div className="bg-black/40 backdrop-blur-sm border border-[#FFD700]/30 rounded-xl px-3 sm:px-4 py-2 sm:py-3 min-w-[54px] sm:min-w-[68px] text-center">
        <div className="text-2xl sm:text-3xl font-extrabold text-white drop-shadow tabular-nums">{v}</div>
      </div>
      <div className="absolute -bottom-4 left-0 right-0 text-center text-[9px] font-mono uppercase tracking-widest text-[#FFD700]/70">{l}</div>
    </div>
  );
}

/* ============================================================ */
/* Pure-typography hero (replaces the JPG poster)               */
/* ============================================================ */
export function Wc2026Hero({ withFacts = true }) {
  const { t } = useT();
  return (
    <section className="relative overflow-hidden rounded-3xl bg-[#020617]" data-testid="wc26-hero">
      {/* Ambient host-color orbs */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[28rem] h-[28rem] rounded-full bg-[#B22234]/25 blur-[150px]" />
        <div className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-[#006847]/25 blur-[150px]" />
        <div className="absolute top-1/3 left-1/3 w-[24rem] h-[24rem] rounded-full bg-[#FFD700]/15 blur-[160px]" />
      </div>
      <div aria-hidden className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "linear-gradient(rgba(255,215,0,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,215,0,1) 1px, transparent 1px)", backgroundSize: "64px 64px" }} />
      <div className="relative px-6 sm:px-10 py-10 sm:py-14 flex flex-col items-center text-center">
        <div className="relative mb-5">
          <div aria-hidden className="absolute inset-0 -m-6 rounded-full bg-[#FFD700]/25 blur-3xl" />
          <Trophy className="relative w-14 h-14 sm:w-16 sm:h-16 text-[#FFD700] drop-shadow-[0_0_24px_rgba(255,215,0,0.7)]" />
        </div>
        <h1
          data-testid="wc26-hero-headline"
          className="font-heading uppercase tracking-tight leading-[0.85] font-extrabold drop-shadow-[0_4px_20px_rgba(0,0,0,0.7)]"
          style={{
            fontSize: "clamp(40px, 8.5vw, 84px)",
            backgroundImage: "linear-gradient(90deg, #B22234 0%, #ffffff 50%, #006847 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          FIFA WORLD CUP
        </h1>
        <div
          data-testid="wc26-hero-year"
          className="font-heading font-extrabold leading-none mt-2"
          style={{
            fontSize: "clamp(72px, 16vw, 168px)",
            color: "#FFD700",
            textShadow: "0 0 60px rgba(255,215,0,0.45), 0 0 24px rgba(255,215,0,0.5)",
          }}
        >
          2026
        </div>
        <div className="mt-6 inline-flex items-center gap-2 sm:gap-3 flex-wrap justify-center" data-testid="wc26-hero-hosts">
          <HostChip flag="🇲🇽" name={t("wc26.host.mexico")} />
          <HostChip flag="🇺🇸" name={t("wc26.host.usa")} />
          <HostChip flag="🇨🇦" name={t("wc26.host.canada")} />
        </div>
        {withFacts && (
          <div className="mt-6 grid grid-cols-4 gap-3 sm:gap-6 w-full max-w-md" data-testid="wc26-hero-facts">
            <Fact n="48" l={t("wc26.fact.teams")} />
            <Fact n="16" l={t("wc26.fact.cities")} />
            <Fact n="104" l={t("wc26.fact.matches")} />
            <Fact n="39"  l={t("wc26.fact.days")} />
          </div>
        )}
      </div>
      <div className="relative grid grid-cols-3 text-center font-heading uppercase tracking-[0.4em] text-[11px] sm:text-sm py-3 border-t border-white/10">
        <span style={{ color: "#B22234" }}>{t("wc26.hero.united")}</span>
        <span className="text-white">{t("wc26.hero.as")}</span>
        <span style={{ color: "#006847" }}>{t("wc26.hero.one")}</span>
      </div>
    </section>
  );
}

function HostChip({ flag, name }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/5 border border-white/15 text-[11px] font-mono uppercase tracking-[0.25em]">
      <span className="text-base">{flag}</span> {name}
    </span>
  );
}

function Fact({ n, l }) {
  return (
    <div className="text-center">
      <div className="font-mono tabular-nums text-2xl sm:text-3xl font-extrabold text-[#FFD700]">{n}</div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-white/50 mt-0.5">{l}</div>
    </div>
  );
}

/* ============================================================ */
/* Notify-me toggle — POST/DELETE /api/wc26/notify-optin        */
/* Wave 2 will ship the server endpoint. Client gracefully falls */
/* back to local-storage so the toggle is functional today.     */
/* ============================================================ */
function NotifyToggle({ mode, accent }) {
  const STORAGE_KEY = `wc26:notify:${mode}`;
  const { user } = useAuth();
  const { t } = useT();
  const [on, setOn] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) === "1"; } catch (_) { return false; }
  });
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!user) { toast.error(t("wc26.notify.signin")); return; }
    setBusy(true);
    const next = !on;
    try {
      if (next) await api.post("/wc26/notify-optin", { mode }).catch(() => {});
      else      await api.delete(`/wc26/notify-optin?mode=${mode}`).catch(() => {});
      try { localStorage.setItem(STORAGE_KEY, next ? "1" : "0"); } catch (_) {}
      setOn(next);
      toast.success(next ? t("wc26.notify.success") : t("wc26.notify.removed"));
    } finally { setBusy(false); }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      disabled={busy}
      data-testid={`wc26-notify-${mode}`}
      className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-[10px] font-mono uppercase tracking-widest border transition-colors ${
        on ? "border-transparent text-[#020617]" : "border-white/15 text-white/70 hover:text-white"
      }`}
      style={on ? { background: accent } : {}}
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : on ? <BellOff className="w-3 h-3" /> : <Bell className="w-3 h-3" />}
      {on ? t("wc26.notify.on") : t("wc26.notify.off")}
    </button>
  );
}

/* ============================================================ */
/* Hub — landing screen                                         */
/* ============================================================ */
export default function Wc2026Hub() {
  const loc = useLocation();
  const { t } = useT();
  const isV2 = loc.pathname.startsWith("/v2");
  const access = useWcAccess();
  const backTo = isV2 ? "/v2/explore" : "/";

  // iter 96 — the WC2026 hub is always browsable so players can read every
  // mode (incl. CTF) and register their pre-launch interest. Actual gameplay
  // entry stays gated by the admin visibility flags (per-mode platform picker)
  // and the backend `require_visible` guard. The launch countdown is still
  // shown inline below. (Previously a hardcoded clock hid the whole hub —
  // including the CTF card and the Register banners — until 24h pre-kickoff.)

  return (
    <div className="min-h-screen bg-[#020617] text-white" data-testid="wc26-hub">
      <Wc2026HeaderBar backTo={backTo} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-16 space-y-5">
        <Wc2026Hero />
        {access.isStaff && !access.publicOpen && (
          <div className="rounded-2xl border border-[#FFD700]/30 bg-[#FFD700]/10 px-4 py-3 text-[11px] text-[#FFD700] flex items-start gap-2" data-testid="wc26-staff-preview">
            <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
            <div>
              <div className="font-bold uppercase tracking-widest text-[10px] font-mono">// {t("wc26.staffPreview")}</div>
              <div className="text-white/70 mt-0.5">{t("wc26.staffPreview.body")}</div>
            </div>
          </div>
        )}
        {!access.kickoffPassed && (
          <div className="rounded-2xl border border-[#FFD700]/20 bg-[#FFD700]/5 px-4 py-3 text-center">
            <div className="text-[9px] font-mono uppercase tracking-widest text-[#FFD700]/60 mb-2">{t("wc26.kickoff")}</div>
            <CountdownClock targetMs={WC_KICKOFF_UTC} label="" />
            <div className="text-[10px] font-mono text-white/30 mt-2">Jun 11, 2026 · 16:00 UTC</div>
          </div>
        )}
        <ModeCard
          to={isV2 ? "/v2/wc2026/tournament" : "/wc2026/tournament"}
          chapter={t("wc26.mode.tournament.chapter")}
          title={t("wc26.mode.tournament.title")}
          tagline={t("wc26.mode.tournament.tagline")}
          schedule={t("wc26.card.sched.tournament")}
          theme="red"
          testId="wc26-hub-card-tournament"
        />
        <ModeCard
          to={isV2 ? "/v2/wc2026/immersion" : "/wc2026/immersion"}
          chapter={t("wc26.mode.immersion.chapter")}
          title={t("wc26.mode.immersion.title")}
          tagline={t("wc26.mode.immersion.tagline")}
          schedule={t("wc26.card.sched.immersion")}
          theme="teal"
          testId="wc26-hub-card-immersion"
        />
        <ModeCard
          to={isV2 ? "/v2/wc2026/dream-team" : "/wc2026/dream-team"}
          chapter={t("wc26.mode.dreamteam.chapter")}
          title={t("wc26.mode.dreamteam.title")}
          tagline={t("wc26.mode.dreamteam.tagline")}
          schedule={t("wc26.card.sched.dreamteam")}
          theme="green"
          testId="wc26-hub-card-dream-team"
        />
        <ModeCard
          to={isV2 ? "/v2/wc2026/ctf" : "/wc2026/ctf"}
          chapter="// Mode IV"
          title="Collect The Flags"
          tagline="Pure national squads · 3-tier mutual matchmaking · 48 flags to collect."
          schedule="Live throughout WC 2026"
          theme="blue"
          testId="wc26-hub-card-ctf"
        />
      </div>
    </div>
  );
}

function Wc2026HeaderBar({ backTo, backLabel }) {
  const { t } = useT();
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-5 pb-3 flex items-center justify-between">
      <Link
        to={backTo}
        data-testid="wc26-back"
        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest text-white/60 hover:text-white"
      >
        <ChevronLeft className="w-3.5 h-3.5" /> {backLabel || t("wc26.back")}
      </Link>
      <Wc2026Logo variant="dark" className="h-7" alt="WC 2026" />
    </div>
  );
}

/* ============================================================ */
/* ModeCard — iter 85 colorful geometric redesign               */
/* ============================================================ */
const CARD_THEMES = {
  red: {
    bg:       "#C32C3F",
    polyDark: "#7A1722",
    polyMid:  "#9A1E2E",
    polyLite: "#E04156",
    chapter:  "#FFD700",
    border:   "#FF3B5C",
    btnBg:    "linear-gradient(180deg, #FFE066 0%, #FFD700 55%, #B89400 100%)",
    btnText:  "#3A1F00",
  },
  teal: {
    bg:       "#0B8FA8",
    polyDark: "#0C4A6E",
    polyMid:  "#0E7490",
    polyLite: "#22D3EE",
    chapter:  "#67E8F9",
    border:   "#22D3EE",
    btnBg:    "linear-gradient(180deg, #93C5FD 0%, #60A5FA 55%, #1D4ED8 100%)",
    btnText:  "#06243A",
  },
  green: {
    bg:       "#12B981",
    polyDark: "#065F46",
    polyMid:  "#047857",
    polyLite: "#34D399",
    chapter:  "#A7F3D0",
    border:   "#34D399",
    btnBg:    "linear-gradient(180deg, #6EE7B7 0%, #34D399 55%, #047857 100%)",
    btnText:  "#064E3B",
  },
  blue: {
    bg:       "#2563EB",
    polyDark: "#1E3A8A",
    polyMid:  "#1D4ED8",
    polyLite: "#60A5FA",
    chapter:  "#BFDBFE",
    border:   "#60A5FA",
    btnBg:    "linear-gradient(180deg, #93C5FD 0%, #3B82F6 55%, #1E3A8A 100%)",
    btnText:  "#0B1A40",
  },
};

function ModeCardGeometry({ theme }) {
  const t = CARD_THEMES[theme] || CARD_THEMES.red;
  return (
    <svg
      aria-hidden
      viewBox="0 0 600 200"
      preserveAspectRatio="none"
      className="absolute inset-0 w-full h-full pointer-events-none"
    >
      <rect x="0" y="0" width="600" height="200" fill={t.bg} />
      {/* Big diagonal slash (dark) — bottom-left to top-right */}
      <polygon points="0,200 220,200 360,0 140,0" fill={t.polyDark} opacity="0.65" />
      {/* Medium accent slash */}
      <polygon points="220,200 320,200 440,0 320,0" fill={t.polyMid} opacity="0.85" />
      {/* Bright thin highlight slash */}
      <polygon points="300,200 340,200 470,0 430,0" fill={t.polyLite} opacity="0.45" />
      {/* Right-side accent block */}
      <polygon points="460,0 600,0 600,90 510,140" fill={t.polyDark} opacity="0.55" />
      <polygon points="500,150 600,130 600,200 440,200" fill={t.polyMid} opacity="0.55" />
      {/* Soft inner radial glow */}
      <rect x="0" y="0" width="600" height="200" fill="url(#card-radial)" opacity="0.22" />
      <defs>
        <radialGradient id="card-radial" cx="35%" cy="40%" r="65%">
          <stop offset="0%"   stopColor="#ffffff" stopOpacity="0.6" />
          <stop offset="100%" stopColor="#000000" stopOpacity="0.4" />
        </radialGradient>
      </defs>
    </svg>
  );
}

/* Per-mode graphic that sits where the lucide icon used to be. */
function ModeIconGraphic({ theme }) {
  if (theme === "red") {
    // WC 2026 trophy logo — the existing PNG already carries the USA-flag motif.
    return (
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
        <div aria-hidden className="absolute inset-0 -m-3 rounded-full bg-[#FFD700]/25 blur-2xl" />
        <img
          src="/wc/wc2026-dark.png"
          alt=""
          className="relative w-full h-full object-contain drop-shadow-[0_4px_10px_rgba(0,0,0,0.55)]"
        />
      </div>
    );
  }
  if (theme === "teal") {
    // Stylized calendar with "26" — evokes the fixture-calendar identity.
    return (
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
        <div aria-hidden className="absolute inset-0 -m-3 rounded-full bg-cyan-300/25 blur-2xl" />
        <div className="relative w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white/95 shadow-[0_8px_18px_rgba(0,0,0,0.35)] flex flex-col overflow-hidden">
          <div className="h-4 sm:h-5 bg-[#0C4A6E] flex items-center justify-around px-2">
            <span className="w-1 h-2.5 bg-[#22D3EE] rounded-sm" />
            <span className="w-1 h-2.5 bg-[#22D3EE] rounded-sm" />
            <span className="w-1 h-2.5 bg-[#22D3EE] rounded-sm" />
          </div>
          <div className="flex-1 flex items-center justify-center font-heading text-2xl sm:text-3xl font-extrabold text-[#0B8FA8]">
            26
          </div>
        </div>
      </div>
    );
  }
  if (theme === "blue") {
    // 3×3 flag-grid mosaic — evokes the 48-flag collection album.
    return (
      <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
        <div aria-hidden className="absolute inset-0 -m-3 rounded-full bg-sky-300/30 blur-2xl" />
        <div className="relative grid grid-cols-3 grid-rows-3 gap-[3px] w-16 h-16 sm:w-20 sm:h-20 p-1.5 rounded-2xl bg-white/95 shadow-[0_8px_18px_rgba(0,0,0,0.35)]">
          {["🇧🇷","🇫🇷","🇪🇸","🇦🇷","🇩🇪","🇯🇵","🇵🇹","🇮🇹","🇲🇽"].map((f, i) => (
            <div key={i} className="rounded-[3px] flex items-center justify-center text-[10px] sm:text-[12px] leading-none">{f}</div>
          ))}
        </div>
      </div>
    );
  }
  // green — flag-shield (Mexico + Canada hosts)
  return (
    <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center">
      <div aria-hidden className="absolute inset-0 -m-3 rounded-full bg-emerald-300/25 blur-2xl" />
      <div className="relative w-[68px] h-[78px] sm:w-[78px] sm:h-[88px]">
        {/* Shield silhouette via clip-path */}
        <div
          className="absolute inset-0 bg-white/95 shadow-[0_8px_18px_rgba(0,0,0,0.35)]"
          style={{ clipPath: "polygon(50% 0%, 100% 12%, 100% 60%, 50% 100%, 0% 60%, 0% 12%)" }}
        />
        {/* Mexico flag stripes (top half) */}
        <div className="absolute inset-x-0 top-0 h-[52%] flex"
             style={{ clipPath: "polygon(50% 0%, 100% 12%, 100% 100%, 0% 100%, 0% 12%)" }}>
          <div className="flex-1 bg-[#006847]" />
          <div className="flex-1 bg-white" />
          <div className="flex-1 bg-[#CE1126]" />
        </div>
        {/* Canada flag (bottom half) — red | white-with-leaf | red */}
        <div className="absolute inset-x-0 bottom-0 h-[55%] flex"
             style={{ clipPath: "polygon(0% 0%, 100% 0%, 100% 45%, 50% 100%, 0% 45%)" }}>
          <div className="flex-1 bg-[#D52B1E]" />
          <div className="flex-1 bg-white flex items-center justify-center">
            <span className="text-[#D52B1E] text-base sm:text-lg leading-none">🍁</span>
          </div>
          <div className="flex-1 bg-[#D52B1E]" />
        </div>
      </div>
    </div>
  );
}

function ModeCard({ to, chapter, title, tagline, schedule, theme = "red", testId }) {
  const t = CARD_THEMES[theme] || CARD_THEMES.red;
  const { t: tr } = useT();
  return (
    <Link
      to={to}
      data-testid={testId}
      className="block group relative overflow-hidden rounded-3xl transition-transform active:scale-[0.99] hover:scale-[1.005]"
      style={{ boxShadow: `0 0 0 2px ${t.border}66, 0 22px 50px -18px ${t.bg}cc` }}
    >
      <ModeCardGeometry theme={theme} />
      <div className="relative p-5 sm:p-6 flex items-stretch gap-4 sm:gap-5 min-h-[170px]">
        <div className="shrink-0 self-center">
          <ModeIconGraphic theme={theme} />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-center">
          <div
            className="text-[10px] font-mono uppercase tracking-[0.3em] drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]"
            style={{ color: t.chapter }}
          >
            // {chapter}
          </div>
          <h2 className="font-heading text-xl sm:text-2xl uppercase tracking-wider leading-[1.05] text-white mt-1 drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">
            {title}
          </h2>
          <div className="text-[12px] sm:text-[13px] text-white/90 mt-1.5 leading-snug drop-shadow-[0_1px_2px_rgba(0,0,0,0.45)]">
            {tagline}
          </div>
          {schedule && (
            <div className="mt-2 inline-flex items-center gap-1.5 w-fit px-2 py-[3px] rounded-full bg-black/30 backdrop-blur-sm border border-white/15">
              <Calendar className="w-2.5 h-2.5 text-white/70" />
              <span className="text-[9px] font-mono uppercase tracking-widest text-white/80">{schedule}</span>
            </div>
          )}
          <span
            className="mt-3 inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-[0.32em] group-hover:gap-2 transition-all w-fit"
            style={{
              background: t.btnBg,
              color: t.btnText,
              boxShadow: "0 4px 12px rgba(0,0,0,0.4), inset 0 1px 0 rgba(255,255,255,0.5)",
            }}
          >
            {tr("wc26.enter")} <ArrowRight className="w-3 h-3" strokeWidth={3} />
          </span>
        </div>
      </div>
    </Link>
  );
}

/* ============================================================ */
/* Locked view — countdown + rule cards with Notify-me toggles  */
/* ============================================================ */
function Wc2026LockedView({ backTo }) {
  const { t } = useT();
  return (
    <div className="min-h-screen bg-[#020617] text-white pb-16" data-testid="wc26-locked">
      <Wc2026HeaderBar backTo={backTo} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 space-y-5">
        <Wc2026Hero />

        {/* Countdown block */}
        <div className="relative rounded-3xl border border-[#FFD700]/25 overflow-hidden" data-testid="wc26-locked-countdown-block">
          <div aria-hidden className="absolute inset-0 bg-gradient-to-br from-[#0F172A] via-[#1E1B4B] to-[#0F172A]" />
          <div aria-hidden className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-80 h-40 rounded-full bg-[#FFD700]/12 blur-[80px]" />
          </div>
          <div className="relative px-6 sm:px-8 py-8 sm:py-10 flex flex-col items-center text-center gap-5">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#FFD700]/10 border border-[#FFD700]/25">
              <Lock className="w-3 h-3 text-[#FFD700]" />
              <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-[#FFD700]">{t("wc26.locked.title")}</span>
            </div>
            <div>
              <CountdownClock targetMs={WC_ACCESS_OPEN_UTC} label={t("wc26.opensIn")} />
              <div className="mt-2 text-[11px] font-mono text-white/40">Jun 10, 2026 · 16:00 UTC</div>
            </div>
            <div className="w-full max-w-xs h-px bg-gradient-to-r from-transparent via-[#FFD700]/20 to-transparent" />
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/35 leading-relaxed text-center">
              {t("wc26.locked.firstMatch")}
            </div>
          </div>
        </div>

        {/* Section heading */}
        <div className="pt-1 space-y-1">
          <div className="text-[10px] font-mono uppercase tracking-[0.35em] text-white/35">// {t("wc26.locked.howItWorks")}</div>
          <h2 className="font-heading text-2xl sm:text-3xl uppercase tracking-wider leading-none text-white">
            {t("wc26.locked.threeModes")}
          </h2>
          <p className="text-[12px] text-white/50">Pick the format that fits how you want to play. You can play all three.</p>
        </div>

        <RuleCard
          mode="tournament"
          icon={Trophy}
          accent="#FFD700"
          chapter={t("wc26.mode.tournament.chapter")}
          title={t("wc26.mode.tournament.title")}
          hook={t("wc26.mode.tournament.hook")}
          rules={[
            t("wc26.rules.tournament.1"),
            t("wc26.rules.tournament.2"),
            t("wc26.rules.tournament.3"),
            t("wc26.rules.tournament.4"),
            t("wc26.rules.tournament.5"),
          ]}
        />

        <RuleCard
          mode="immersion"
          icon={Calendar}
          accent="#38BDF8"
          chapter={t("wc26.mode.immersion.chapter")}
          title={t("wc26.mode.immersion.title")}
          hook={t("wc26.mode.immersion.hook")}
          rules={[
            t("wc26.rules.immersion.1"),
            t("wc26.rules.immersion.2"),
            t("wc26.rules.immersion.3"),
            t("wc26.rules.immersion.4"),
            t("wc26.rules.immersion.5"),
            t("wc26.rules.immersion.6"),
          ]}
        />

        <RuleCard
          mode="dream-team"
          icon={Shield}
          accent="#10B981"
          chapter={t("wc26.mode.dreamteam.chapter")}
          title={t("wc26.mode.dreamteam.title")}
          hook={t("wc26.mode.dreamteam.hook")}
          rules={[
            t("wc26.rules.dreamteam.7"),
            t("wc26.rules.dreamteam.8"),
            t("wc26.rules.dreamteam.1"),
            t("wc26.rules.dreamteam.2"),
            t("wc26.rules.dreamteam.3"),
            t("wc26.rules.dreamteam.4"),
            t("wc26.rules.dreamteam.5"),
            t("wc26.rules.dreamteam.6"),
          ]}
        />

        <div className="rounded-2xl border border-white/8 bg-white/3 p-4 text-[11px] text-white/45 text-center" data-testid="wc26-locked-footer">
          {t("wc26.locked.footer")} <span className="text-[#FFD700]">{t("wc26.hub.unitedAsOne")}</span>
        </div>
      </div>
    </div>
  );
}

function RuleCard({ mode, icon: Icon, accent, chapter, title, hook, rules }) {
  const num = chapter.split("·")[0].trim(); // "01", "02", "03"
  return (
    <div
      className="rounded-3xl border overflow-hidden"
      style={{ borderColor: `${accent}25`, background: "rgba(15,23,42,0.7)" }}
      data-testid={`wc26-rule-${num}`}
    >
      {/* Card header with gradient accent strip */}
      <div
        className="px-5 sm:px-6 pt-5 pb-4 sm:pt-6"
        style={{ background: `linear-gradient(135deg, ${accent}10 0%, transparent 60%)` }}
      >
        <div className="flex items-start justify-between gap-3">
          {/* Icon + labels */}
          <div className="flex items-center gap-4 flex-1 min-w-0">
            {/* Large icon with glow halo */}
            <div className="relative shrink-0">
              <div
                aria-hidden
                className="absolute inset-0 -m-3 rounded-full blur-xl"
                style={{ background: `${accent}35` }}
              />
              <div
                className="relative w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center shadow-lg"
                style={{
                  background: `linear-gradient(135deg, ${accent}20 0%, ${accent}08 100%)`,
                  border: `1.5px solid ${accent}30`,
                }}
              >
                <Icon className="w-7 h-7 sm:w-8 sm:h-8" style={{ color: accent }} />
              </div>
            </div>
            <div className="min-w-0">
              {/* Chapter number pill */}
              <div
                className="inline-flex items-center gap-1 px-2 py-[2px] rounded-full text-[9px] font-mono font-bold uppercase tracking-[0.3em] mb-1"
                style={{ background: `${accent}18`, color: accent }}
              >
                // {chapter}
              </div>
              <h3 className="font-heading text-lg sm:text-xl uppercase tracking-wider leading-none text-white">
                {title}
              </h3>
              {hook && (
                <p className="mt-1 text-[11px] italic leading-relaxed" style={{ color: `${accent}cc` }}>
                  "{hook}"
                </p>
              )}
            </div>
          </div>
          {/* Notify toggle */}
          <div className="shrink-0 pt-0.5">
            <NotifyToggle mode={mode} accent={accent} />
          </div>
        </div>
      </div>

      {/* Rules list */}
      <div className="px-5 sm:px-6 pb-5 sm:pb-6">
        <ul className="space-y-2.5 border-t pt-4" style={{ borderColor: `${accent}15` }}>
          {rules.map((r, i) => (
            <li key={i} className="flex items-start gap-3 text-[12px] text-white/80 leading-relaxed">
              <span
                className="mt-[5px] w-1.5 h-1.5 rounded-full shrink-0"
                style={{ background: accent, boxShadow: `0 0 6px ${accent}80` }}
              />
              <span>{r}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/* ============================================================ */
/* MODE_SPECS — single source of truth for the 3 dedicated      */
/* per-mode entry pages. Eliminates the old shared "authentic"   */
/* page that listed Tournament + Immersion together.             */
/* ============================================================ */
const MODE_SPECS = {
  tournament: {
    accent: "#FFD700",
    icon: Trophy,
    chapterKey: "wc26.mode.tournament.chapter",
    titleKey:   "wc26.mode.tournament.title",
    hookKey:    "wc26.mode.tournament.hook",
    feelKey:    "wc26.tournament.feel",
    rulesKeys: [
      "wc26.rules.tournament.1", "wc26.rules.tournament.2", "wc26.rules.tournament.3",
      "wc26.rules.tournament.4", "wc26.rules.tournament.5",
    ],
    stats: [
      { n: "48",  labelKey: "wc26.tournament.stat.1" },
      { n: "12",  labelKey: "wc26.tournament.stat.2" },
      { n: "32",  labelKey: "wc26.tournament.stat.3" },
    ],
    schedKeys: ["wc26.tournament.sched.1", "wc26.tournament.sched.2"],
    paths: { mobile: "/v2/world-cup-tourney/mobile", console: "/v2/world-cup-tourney/console" },
    testIdRoot: "wc26-mode-tournament",
  },
  immersion: {
    accent: "#38BDF8",
    icon: Calendar,
    chapterKey: "wc26.mode.immersion.chapter",
    titleKey:   "wc26.mode.immersion.title",
    hookKey:    "wc26.mode.immersion.hook",
    feelKey:    "wc26.immersion.feel",
    rulesKeys: [
      "wc26.rules.immersion.1", "wc26.rules.immersion.2", "wc26.rules.immersion.3",
      "wc26.rules.immersion.4", "wc26.rules.immersion.5", "wc26.rules.immersion.6",
    ],
    stats: [
      { n: "33",    labelKey: "wc26.immersion.stat.1" },
      { n: "1,000", labelKey: "wc26.immersion.stat.2" },
      { n: "30%",   labelKey: "wc26.immersion.stat.3" },
    ],
    schedKeys: [
      "wc26.immersion.sched.1", "wc26.immersion.sched.2", "wc26.immersion.sched.3",
      "wc26.immersion.sched.4", "wc26.immersion.sched.5", "wc26.immersion.sched.6",
    ],
    paths: { mobile: "/v2/wc2026/league/mobile", console: "/v2/wc2026/league/console" },
    testIdRoot: "wc26-mode-immersion",
  },
  "dream-team": {
    accent: "#10B981",
    icon: Shield,
    chapterKey: "wc26.mode.dreamteam.chapter",
    titleKey:   "wc26.mode.dreamteam.title",
    hookKey:    "wc26.mode.dreamteam.hook",
    feelKey:    "wc26.dreamteam.feel",
    rulesKeys: [
      { k: "wc26.rules.dreamteam.7", highlight: true },
      { k: "wc26.rules.dreamteam.8", highlight: true },
      "wc26.rules.dreamteam.1", "wc26.rules.dreamteam.2", "wc26.rules.dreamteam.3",
      "wc26.rules.dreamteam.4", "wc26.rules.dreamteam.5", "wc26.rules.dreamteam.6",
    ],
    stats: [
      { n: "20", labelKey: "wc26.dreamteam.stat.1" },
      { n: "24", labelKey: "wc26.dreamteam.stat.2" },
      { n: "3",  labelKey: "wc26.dreamteam.stat.3" },
    ],
    schedKeys: ["wc26.dreamteam.sched.1", "wc26.dreamteam.sched.2", "wc26.dreamteam.sched.3"],
    paths: { mobile: "/v2/dream-team/mobile", console: "/v2/dream-team/console" },
    testIdRoot: "wc26-mode-dreamteam",
  },
};

/* ============================================================ */
/* Wc2026ModeEntry — premium dedicated landing for ONE mode      */
/* ============================================================ */
function Wc2026ModeEntry({ modeKey }) {
  const loc = useLocation();
  const { t } = useT();
  const isV2 = loc.pathname.startsWith("/v2");
  const backTo = isV2 ? "/v2/wc2026" : "/wc2026";
  const spec = MODE_SPECS[modeKey];
  // iter 93 — admin visibility gate. Check both platforms; the gameplay
  // surface (stats, schedule, platform picker) reveals if either flag is on
  // OR the viewer is staff.
  const vMobile = useWcModeVisibility(modeKey, "mobile");
  const vConsole = useWcModeVisibility(modeKey, "console");
  const visLoading = vMobile.loading || vConsole.loading;
  const isStaff = vMobile.isStaff || vConsole.isStaff;
  const effectivelyVisible =
    isStaff || !!vMobile.effective || !!vConsole.effective;

  // iter 96 — the mode page itself (hero, rules, schedule and the pre-launch
  // Register banner) is always viewable. Only the gameplay entry (the platform
  // picker below) stays behind the admin visibility flag via `effectivelyVisible`.
  if (!spec) return null;

  const { accent, icon: Icon } = spec;

  return (
    <div className="min-h-screen bg-[#020617] text-white" data-testid={spec.testIdRoot}>
      <Wc2026HeaderBar backTo={backTo} backLabel={t("wc26.entry.backToHub")} />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pb-20 space-y-5">

        {/* ── Cinematic hero ── */}
        <section className="relative overflow-hidden rounded-3xl" style={{ border: `1px solid ${accent}30` }}>
          <div aria-hidden className="absolute inset-0" style={{ background: `radial-gradient(120% 90% at 50% -10%, ${accent}22 0%, #020617 60%)` }} />
          <div aria-hidden className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: `linear-gradient(${accent} 1px, transparent 1px), linear-gradient(90deg, ${accent} 1px, transparent 1px)`, backgroundSize: "56px 56px" }} />
          <div className="relative px-6 sm:px-8 py-8 sm:py-10 flex flex-col items-center text-center">
            <div className="relative mb-4">
              <div aria-hidden className="absolute inset-0 -m-5 rounded-full blur-2xl" style={{ background: `${accent}45` }} />
              <div
                className="relative w-20 h-20 sm:w-24 sm:h-24 rounded-3xl flex items-center justify-center shadow-2xl"
                style={{ background: `linear-gradient(135deg, ${accent}25 0%, ${accent}08 100%)`, border: `1.5px solid ${accent}45` }}
              >
                <Icon className="w-10 h-10 sm:w-12 sm:h-12" style={{ color: accent }} />
              </div>
            </div>
            <div
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-mono font-bold uppercase tracking-[0.35em] mb-3"
              style={{ background: `${accent}1a`, color: accent }}
            >
              // {t(spec.chapterKey)}
            </div>
            <h1 className="font-heading text-3xl sm:text-5xl uppercase tracking-wider leading-[0.95] text-white drop-shadow-[0_2px_12px_rgba(0,0,0,0.6)]">
              {t(spec.titleKey)}
            </h1>
            <p className="mt-3 text-[13px] sm:text-base italic max-w-md leading-relaxed" style={{ color: `${accent}dd` }}>
              "{t(spec.hookKey)}"
            </p>
          </div>
        </section>

        {/* iter 96 — Tournament & Immersion do a REAL join (draws a team /
            assigns a shard, shows in admin Registered Players). Dream Team and
            CTF have no roster, so they keep the pre-launch interest banner. */}
        {(modeKey === "tournament" || modeKey === "immersion")
          ? <WcJoinCard mode={modeKey} accent={accent} />
          : <WcRegisterBanner mode={modeKey} />}

        {/* iter 99 — prominent "Enter & Play" platform picker RIGHT under the
            join card for the two roster modes, so players don't have to scroll
            past the rules/schedule to find the small picker at the bottom. */}
        {(modeKey === "tournament" || modeKey === "immersion") && !visLoading && effectivelyVisible && (
          <div
            className="rounded-3xl border p-5 sm:p-6"
            style={{ borderColor: `${accent}45`, background: `linear-gradient(160deg, ${accent}1c 0%, rgba(15,23,42,0.7) 100%)` }}
            data-testid={`${spec.testIdRoot}-play-top`}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[11px] font-mono uppercase tracking-[0.3em] font-extrabold" style={{ color: accent }}>
                ▶ {t("wc26.entry.pickPlatform")}
              </span>
            </div>
            <p className="text-[11px] text-white/55 mb-1">{t("wc26.entry.platformNote")}</p>
            <PlatformButtons paths={spec.paths} accent={accent} v1Hint={!isV2} testIdRoot={`${spec.testIdRoot}-top`} t={t} />
          </div>
        )}

        {/* ── Stats strip ── */}
        <div className="grid grid-cols-3 gap-2.5">
          {spec.stats.map((s, i) => (
            <div
              key={i}
              className="rounded-2xl border p-3 sm:p-4 text-center"
              style={{ borderColor: `${accent}22`, background: `linear-gradient(160deg, ${accent}10 0%, rgba(15,23,42,0.6) 100%)` }}
              data-testid={`${spec.testIdRoot}-stat-${i}`}
            >
              <div className="font-heading tabular-nums text-2xl sm:text-3xl font-extrabold" style={{ color: accent }}>{s.n}</div>
              <div className="text-[9px] sm:text-[10px] font-mono uppercase tracking-widest text-white/45 mt-1 leading-tight">{t(s.labelKey)}</div>
            </div>
          ))}
        </div>

        {/* ── Format (rules) ── */}
        <SectionBlock accent={accent} icon={ListIcon} label={t("wc26.entry.format")}>
          <ul className="space-y-2.5">
            {spec.rulesKeys.map((entry, i) => {
              const isObj = typeof entry === "object" && entry !== null;
              const key = isObj ? entry.k : entry;
              const highlight = isObj && entry.highlight;
              if (highlight) {
                return (
                  <li
                    key={i}
                    data-testid={`wc26-rule-highlight-${i}`}
                    className="relative flex items-start gap-3 rounded-xl px-3 py-2.5 border-2"
                    style={{
                      borderColor: "#FFD700",
                      background: "linear-gradient(135deg, rgba(255,215,0,0.18) 0%, rgba(255,215,0,0.05) 100%)",
                      boxShadow: "0 0 20px rgba(255,215,0,0.25), inset 0 0 12px rgba(255,215,0,0.08)",
                    }}
                  >
                    <span
                      className="absolute -top-2 left-3 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[8px] font-mono uppercase tracking-[0.25em] font-extrabold bg-[#FFD700] text-[#020617]"
                    >
                      <Sparkles className="w-2 h-2" /> New rule
                    </span>
                    <ShieldCheck
                      className="w-4 h-4 shrink-0 mt-[2px]"
                      style={{ color: "#FFD700", filter: "drop-shadow(0 0 6px rgba(255,215,0,0.7))" }}
                    />
                    <span className="text-[13px] text-white font-semibold leading-relaxed">
                      {t(key)}
                    </span>
                    <span
                      aria-hidden
                      className="absolute inset-0 rounded-xl pointer-events-none animate-pulse"
                      style={{ boxShadow: "inset 0 0 0 1px rgba(255,215,0,0.45)" }}
                    />
                  </li>
                );
              }
              return (
                <li key={i} className="flex items-start gap-3 text-[12.5px] text-white/80 leading-relaxed">
                  <span className="mt-[6px] w-1.5 h-1.5 rounded-full shrink-0" style={{ background: accent, boxShadow: `0 0 6px ${accent}90` }} />
                  <span>{t(key)}</span>
                </li>
              );
            })}
          </ul>
        </SectionBlock>

        {/* ── Schedule timeline ── */}
        <SectionBlock accent={accent} icon={Calendar} label={t("wc26.entry.schedule")}>
          <div className="space-y-0">
            {spec.schedKeys.map((k, i) => {
              const isLast = i === spec.schedKeys.length - 1;
              return (
                <div key={k} className="flex items-stretch gap-3">
                  {/* Rail */}
                  <div className="flex flex-col items-center">
                    <span className="w-2.5 h-2.5 rounded-full shrink-0 mt-1.5" style={{ background: accent, boxShadow: `0 0 8px ${accent}` }} />
                    {!isLast && <span className="w-px flex-1 my-1" style={{ background: `${accent}30` }} />}
                  </div>
                  <div className={`flex items-baseline justify-between gap-3 flex-1 ${isLast ? "pb-0" : "pb-4"}`}>
                    <span className="text-[12.5px] text-white/85">{t(`${k}.label`)}</span>
                    <span className="text-[11px] font-mono font-bold tabular-nums shrink-0" style={{ color: accent }}>{t(`${k}.date`)}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </SectionBlock>

        {/* ── What it feels like ── */}
        <SectionBlock accent={accent} icon={Sparkles} label={t("wc26.entry.feel")}>
          <p className="text-[12.5px] text-white/70 leading-relaxed">{t(spec.feelKey)}</p>
        </SectionBlock>

        {/* ── Platform picker / enter ── iter 93: locked behind admin gate */}
        {visLoading ? (
          <div className="rounded-3xl border border-white/10 bg-[#0F172A]/60 p-5 text-center text-[10px] font-mono uppercase tracking-widest text-white/40">
            Loading…
          </div>
        ) : !effectivelyVisible ? (
          <WcLockedNotice accent={accent} />
        ) : (modeKey === "tournament" || modeKey === "immersion") ? null : (
          <div
            className="rounded-3xl border p-5 sm:p-6"
            style={{ borderColor: `${accent}30`, background: `linear-gradient(160deg, ${accent}12 0%, rgba(15,23,42,0.7) 100%)` }}
          >
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: accent }}>// {t("wc26.entry.pickPlatform")}</span>
              {isStaff && (!vMobile.visible || !vConsole.visible) && (
                <span className="text-[9px] font-mono uppercase tracking-[0.25em] px-1.5 py-0.5 rounded bg-[#FFD700]/15 text-[#FFD700] border border-[#FFD700]/40" data-testid={`wc-staff-preview-${modeKey}`}>
                  Staff preview
                </span>
              )}
            </div>
            <p className="text-[11px] text-white/45 mb-1">{t("wc26.entry.platformNote")}</p>
            <PlatformButtons paths={spec.paths} accent={accent} v1Hint={!isV2} testIdRoot={spec.testIdRoot} t={t} />
          </div>
        )}
      </div>
    </div>
  );
}

/* Section wrapper used by mode-entry pages */
function SectionBlock({ accent, icon: Icon, label, children }) {
  return (
    <section className="rounded-3xl border bg-[#0F172A]/60 overflow-hidden" style={{ borderColor: `${accent}1f` }}>
      <div className="px-5 sm:px-6 pt-4 pb-3 flex items-center gap-2 border-b" style={{ borderColor: `${accent}14` }}>
        {Icon && <Icon className="w-3.5 h-3.5" style={{ color: accent }} />}
        <span className="text-[10px] font-mono uppercase tracking-[0.3em]" style={{ color: accent }}>{label}</span>
      </div>
      <div className="px-5 sm:px-6 py-4">{children}</div>
    </section>
  );
}

/* ============================================================ */
/* Thin route wrappers — one per mode                           */
/* ============================================================ */
export function Wc2026Tournament() { return <Wc2026ModeEntry modeKey="tournament" />; }
export function Wc2026Immersion()  { return <Wc2026ModeEntry modeKey="immersion" />; }
export function Wc2026DreamTeam()  { return <Wc2026ModeEntry modeKey="dream-team" />; }

/* Back-compat: the old combined "Authentic" sub-hub now redirects
   to the hub so stale deep-links / bookmarks don't break. */
export function Wc2026Authentic() {
  const loc = useLocation();
  const isV2 = loc.pathname.startsWith("/v2");
  return <Navigate to={isV2 ? "/v2/wc2026" : "/wc2026"} replace />;
}

/* ============================================================ */
/* Gate wrapper for deep links                                  */
/* ============================================================ */
// iter 96 — resolve the WC mode from a deep-link path so the gate can consult
// the matching admin visibility flag.
function modeFromPath(pathname) {
  if (pathname.includes("world-cup-tourney")) return "tournament";
  if (pathname.includes("dream-team")) return "dream-team";
  if (pathname.includes("wc-immersion") || pathname.includes("wc2026/league")) return "immersion";
  return null;
}

export function WcGateRoute({ children }) {
  const access = useWcAccess();
  const loc = useLocation();
  const mode = modeFromPath(loc.pathname);
  // Consult the admin visibility flag for this mode (both platforms). Hooks
  // must run unconditionally, so we always call them with the resolved mode.
  const vMobile = useWcModeVisibility(mode || "tournament", "mobile");
  const vConsole = useWcModeVisibility(mode || "tournament", "console");
  const adminOpen = !!mode && (!!vMobile.effective || !!vConsole.effective);
  // Staff, the public launch window, OR an admin-opened gate all grant entry.
  // The backend `require_visible` guard remains the source of truth for writes.
  if (access.isStaff || access.publicOpen || adminOpen) return children;
  if (vMobile.loading || vConsole.loading) return null;
  const backTo = loc.pathname.startsWith("/v2") ? "/v2/explore" : "/";
  return <Wc2026LockedView backTo={backTo} />;
}

function PlatformButtons({ paths, accent, v1Hint, testIdRoot, t }) {
  const navigate = useNavigate();
  if (v1Hint) {
    return (
      <div className="mt-4 text-[11px] text-white/60 space-x-2">
        <span>{t("wc26.platform.openOnV2")}</span>
        <Link to={paths.mobile} className="underline" style={{ color: accent }} data-testid={`${testIdRoot}-v2link-mobile`}>{t("wc26.platform.mobile")}</Link>
        <span className="text-white/30">·</span>
        <Link to={paths.console} className="underline" style={{ color: accent }} data-testid={`${testIdRoot}-v2link-console`}>{t("wc26.platform.console")}</Link>
      </div>
    );
  }
  return (
    <div className="mt-5 flex gap-2">
      {[["mobile", Smartphone], ["console", Monitor]].map(([p, Icon]) => (
        <button
          key={p}
          type="button"
          onClick={() => navigate(paths[p])}
          data-testid={`${testIdRoot}-${p}`}
          className="flex-1 inline-flex items-center justify-center gap-2 rounded-xl border px-4 py-3 text-[12px] font-bold uppercase tracking-wider text-white transition-all hover:scale-[1.01] active:scale-[0.99]"
          style={{ borderColor: `${accent}35`, background: `${accent}10` }}
        >
          <Icon className="w-4 h-4" style={{ color: accent }} />
          {p === "mobile" ? t("wc26.platform.mobile") : t("wc26.platform.console")}
          <ArrowRight className="w-3.5 h-3.5 ml-auto" style={{ color: accent }} />
        </button>
      ))}
    </div>
  );
}
